/**
 * Purchase Management Component
 * Handles the creation and approval of purchase orders
 */

const PurchaseManagement = {
    // Diseño A (oct. 2026): one page for warehouse, Administración and RRHH — figures,
    // status tabs, and each row with its items, reason, total, who approved and the
    // action that belongs to whoever is looking.
    _tab: 'pending',
    _search: '',
    STATUS: {
        pending: ['Por firmar', 'is-warning'],
        approved: ['Aprobada · por recibir', 'is-primary'],
        received: ['Recibida', 'is-success'],
        rejected: ['Rechazada', 'is-danger']
    },

    canCreate() { return AuthManager.canManageWarehouse(); },
    canApprove() { return AuthManager.isAdmin() || AuthManager.isRRHH(); },
    canReceive() { return AuthManager.canManageWarehouse(); },
    canView() { return AuthManager.canManageWarehouse() || AuthManager.hasModule('almacen') || AuthManager.isAdmin() || AuthManager.isRRHH(); },

    _money(value) {
        return (Number(value) || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
    },
    _day(value) {
        const d = new Date(value);
        if (isNaN(d)) return '';
        const today = new Date();
        const diff = Math.floor((new Date(today.toDateString()) - new Date(d.toDateString())) / 86400000);
        return diff === 0 ? 'Hoy' : diff === 1 ? 'Ayer' : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    },
    // Academic year: from 1 September.
    _courseStart() {
        const now = new Date();
        return new Date(now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1, 8, 1);
    },

    setTab(tab) { this._tab = tab; this.refresh(); },
    setSearch(value) {
        this._search = String(value || '').trim().toLowerCase();
        this.refresh();
        const input = document.getElementById('whp-search');
        if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
    },

    render() {
        if (!this.canView()) return '<p class="whp-empty">No tienes permiso para ver esta sección.</p>';
        const orders = WarehouseService.getAllOrders();
        const since = this._courseStart().getTime();
        const inCourse = o => new Date(o.updatedAt || o.createdAt).getTime() >= since;
        const pending = orders.filter(o => o.status === 'pending');
        const approved = orders.filter(o => o.status === 'approved');
        const received = orders.filter(o => o.status === 'received' && inCourse(o));
        const rejected = orders.filter(o => o.status === 'rejected' && inCourse(o));
        const spent = received.reduce((sum, o) => sum + (Number(o.totalCost) || 0), 0);
        const waiting = pending.reduce((sum, o) => sum + (Number(o.totalCost) || 0), 0);
        const tabs = [['pending', `Por firmar · ${pending.length}`], ['approved', `Por recibir · ${approved.length}`], ['received', 'Recibidas'], ['rejected', 'Rechazadas'], ['all', 'Todas']];
        const q = this._search;
        const list = orders
            .filter(o => this._tab === 'all' || o.status === this._tab)
            .filter(o => !q || [o.requesterName, o.reason, ...(o.items || []).map(i => i.name)].join(' ').toLowerCase().includes(q))
            .sort((x, y) => new Date(y.createdAt || 0) - new Date(x.createdAt || 0));
        return `
            <div class="purchase-management-container whp-view">
                <div class="whp-figs">
                    <div class="wh-fig is-warn"><span class="wh-fig-label">Por firmar</span><b>${pending.length}</b><small>${pending.length ? `${this._money(waiting)} esperando firma` : 'Nada pendiente'}</small></div>
                    <div class="wh-fig"><span class="wh-fig-label">Por recibir</span><b>${approved.length}</b><small>aprobadas, falta que lleguen</small></div>
                    <div class="wh-fig"><span class="wh-fig-label">Gastado este curso</span><b class="is-ok">${this._money(spent)}</b><small>${received.length} ${received.length === 1 ? 'compra recibida' : 'compras recibidas'}</small></div>
                    <div class="wh-fig"><span class="wh-fig-label">Rechazadas</span><b>${rejected.length}</b><small>este curso</small></div>
                </div>
                <section class="wh-card">
                    <div class="whp-bar">
                        <div class="whp-tabs" role="tablist" aria-label="Estado de las compras">
                            ${tabs.map(([id, label]) => `<button type="button" role="tab" class="${this._tab === id ? 'is-on' : ''}" aria-selected="${this._tab === id}" ${UIEvents.attr("click", function(event,captured){PurchaseManagement.setTab(String(captured[0]));}, [id])}>${label}</button>`).join('')}
                        </div>
                        <span class="whp-spacer"></span>
                        <input type="search" id="whp-search" class="whr-input" placeholder="Buscar artículo, motivo o quién la pide" aria-label="Buscar compras" value="${Utils.sanitizeHTML(this._search)}"
                            ${UIEvents.attr("input", function(event,captured){PurchaseManagement.setSearch(this.value);}, [])}>
                        ${this.canCreate() ? `<button type="button" class="whr-btn is-go whp-new" ${UIEvents.attr("click", function(event,captured){PurchaseManagement.openRequestModal();}, [])}><i class="fas fa-plus"></i> Nueva compra</button>` : ''}
                    </div>
                    <div class="whp-list">
                        ${list.length ? list.map(o => this.renderOrderRow(o)).join('') : `<p class="whp-empty">${orders.length ? 'No hay compras en esta pestaña.' : 'Aún no hay compras.'}</p>`}
                    </div>
                </section>
            </div>
        `;
    },

    renderOrderRow(order) {
        const esc = v => Utils.sanitizeHTML(String(v ?? ''));
        const [label, cls] = this.STATUS[order.status] || [order.status, 'is-secondary'];
        const items = (order.items || []).map(i => `${i.name} ×${i.qty}`).join(' · ');
        const approvals = order.approvals || {};
        const signed = [approvals.admin ? 'Administración' : '', approvals.rrhh ? 'RRHH' : ''].filter(Boolean);
        let who = '';
        if (order.status === 'rejected') who = order.rejectionReason ? `Motivo: «${esc(order.rejectionReason)}»` : '';
        else if (signed.length) who = `Aprobada por ${esc(signed.join(' y '))}`;
        else if (order.status === 'pending') who = 'Sin firmar';
        let actions = '';
        if (order.status === 'pending' && this.canApprove()) {
            const role = AuthManager.isAdmin() ? 'Administración' : 'RRHH';
            actions = `<button type="button" class="whr-btn is-go" ${UIEvents.attr("click", function(event,captured){PurchaseManagement.approve(String(captured[0]));}, [order.id])}>Firmar como ${role}</button>
                <button type="button" class="whr-btn is-no" ${UIEvents.attr("click", function(event,captured){PurchaseManagement.reject(String(captured[0]));}, [order.id])}>Rechazar</button>`;
        } else if (order.status === 'approved' && this.canReceive()) {
            actions = `<button type="button" class="whr-btn is-go" ${UIEvents.attr("click", function(event,captured){PurchaseManagement.receiveOrder(String(captured[0]));}, [order.id])}>Registrar llegada</button>`;
        }
        return `
            <article class="whp-row">
                <div class="whp-when"><b>${esc(this._day(order.createdAt))}</b><small>${esc(order.requesterName || '')}</small></div>
                <div class="whp-what"><span>${esc(items) || '—'}</span>${order.reason ? `<small>«${esc(order.reason)}»</small>` : ''}</div>
                <b class="whp-total">${esc(this._money(order.totalCost))}</b>
                <div class="whp-state"><span class="wh-status-badge ${cls}">${esc(label)}</span>${who ? `<small>${who}</small>` : ''}</div>
                <div class="whp-actions">${actions}</div>
            </article>
        `;
    },

    // ===================================
    // Logic & Events
    // ===================================

    init() {
        console.log('PurchaseManagement init');
        this.render(); // Ensure it renders if called directly, though typically handled by WarehouseView
    },

    openRequestModal() {
        // We need to attach the additemRow function to global scope or inside modal's context
        // Since ModalManager inserts HTML string, `PurchaseManagement` must be global (and it is)

        const content = `
            <div id="purchase-request-wrapper" class="dnr-proposal wh-form-dnr">
                <form id="purchase-request-form">
                    <div class="items-list" id="purchase-items-container">
                        <!-- Dynamic Items will be added here -->
                    </div>
                    <div class="mb-3">
                        <button type="button" class="btn btn-sm btn-secondary" ${UIEvents.attr("click", function(event,captured){PurchaseManagement.additemRow();}, [])}>
                            <i class="fas fa-plus"></i> Añadir artículo
                        </button>
                    </div>

                    <div class="form-group">
                        <label>Por qué hace falta</label>
                        <textarea class="form-control" name="reason" rows="2" required></textarea>
                    </div>

                    <div class="total-preview text-end h5">
                        Total: <span id="purchase-total-display">0.00</span> €
                    </div>
                </form>
            </div>
        `;

        ModalManager.show('Nueva compra', content, [
            { text: 'Cancelar', class: 'btn-secondary', action: () => ModalManager.close() },
            { text: 'Enviar a firmar', class: 'btn-primary', action: () => PurchaseManagement.submitRequest() }
        ]);

        // Add first row after modal is shown (timeout to ensure DOM existence)
        setTimeout(() => {
            this.additemRow();
        }, 100);
    },

    additemRow() {
        const container = document.getElementById('purchase-items-container');
        if (!container) return;

        const rowId = crypto.randomUUID();
        const div = document.createElement('div');
        div.className = 'purchase-item-row card p-2 mb-2 bg-light';
        SafeHTML.set(div, `
            <div class="row g-2 align-items-center">
                <div class="col-md-5">
                    <input type="text" class="form-control form-control-sm" name="name_${rowId}" placeholder="Nombre del artículo" required>
                </div>
                <div class="col-md-2">
                    <input type="number" class="form-control form-control-sm" name="qty_${rowId}" placeholder="Cant." value="1" min="1" required ${UIEvents.attr("input", function(event,captured){PurchaseManagement.calcTotal();}, [])}>
                </div>
                <div class="col-md-3">
                    <input type="number" class="form-control form-control-sm" name="price_${rowId}" placeholder="Precio Unit." step="0.01" required ${UIEvents.attr("input", function(event,captured){PurchaseManagement.calcTotal();}, [])}>
                </div>
                <div class="col-md-2 text-end">
                    <button type="button" class="btn btn-sm btn-icon text-danger" ${UIEvents.attr("click", function(event,captured){this.closest('.purchase-item-row').remove();PurchaseManagement.calcTotal();}, [])}>
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </div>
        `);
        container.appendChild(div);
    },

    calcTotal() {
        const container = document.getElementById('purchase-items-container');
        if (!container) return;

        let total = 0;
        const rows = container.querySelectorAll('.purchase-item-row');
        rows.forEach(row => {
            const qtyInput = row.querySelector('input[name^="qty_"]');
            const priceInput = row.querySelector('input[name^="price_"]');

            // Handle null inputs gracefully
            if (qtyInput && priceInput) {
                const qty = parseFloat(qtyInput.value) || 0;
                const price = parseFloat(priceInput.value) || 0;
                total += qty * price;
            }
        });

        const display = document.getElementById('purchase-total-display');
        if (display) display.textContent = total.toFixed(2);
    },

    async submitRequest() {
        const form = document.getElementById('purchase-request-form');
        if (!form) return;

        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        const items = [];
        const container = document.getElementById('purchase-items-container');

        // Manual Validation: Check if there are items
        const rows = container.querySelectorAll('.purchase-item-row');
        if (rows.length === 0) {
            Utils.showToast('Añade al menos un artículo', 'warning');
            return;
        }

        rows.forEach(row => {
            const qtyInput = row.querySelector('input[name^="qty_"]');
            const priceInput = row.querySelector('input[name^="price_"]');
            const nameInput = row.querySelector('input[name^="name_"]');

            if (qtyInput && priceInput && nameInput) {
                items.push({
                    name: nameInput.value,
                    qty: parseInt(qtyInput.value),
                    price: parseFloat(priceInput.value),
                    link: '' // Optional
                });
            }
        });

        const reason = form.querySelector('textarea[name="reason"]').value;

        const data = {
            requesterName: AuthManager.getCurrentUser().name,
            reason: reason,
            items: items
        };

        if (!await WarehouseService.createOrder(data) || !form.isConnected) return;
        Utils.showToast('Compra enviada a firmar', 'success');
        ModalManager.close();

        // Refresh view
        this.refresh();
    },

    refresh() {
        const container = document.getElementById('purchase-orders-container');
        if (container) SafeHTML.set(container, this.render());
        else if (typeof WarehouseView !== 'undefined') WarehouseView.switchTab('orders');
    },

    async approve(orderId) {
        const role = AuthManager.isAdmin() ? 'Administración' : 'RRHH';
        const result = await SafeHTML.fire({ title: '¿Aprobar esta compra?', text: `Firmas como ${role}. Almacén podrá registrar su llegada.`, icon: 'question',
            showCancelButton: true, confirmButtonText: 'Sí, aprobar', cancelButtonText: 'Volver', confirmButtonColor: '#0E6B62', reverseButtons: true });
        if (!result.isConfirmed) return;
        if (!await WarehouseService.approveOrder(orderId)) return;
        Utils.showToast('Compra aprobada', 'success');
        this.refresh();
    },
    async reject(orderId) {
        const result = await SafeHTML.fire({ title: '¿Rechazar esta compra?', input: 'textarea', inputLabel: 'Motivo (lo verá almacén)', inputPlaceholder: 'Ej.: no hay presupuesto este trimestre',
            showCancelButton: true, confirmButtonText: 'Rechazar', cancelButtonText: 'Volver', confirmButtonColor: '#8E2A21', reverseButtons: true,
            inputValidator: value => (!value || !value.trim()) ? 'Escribe el motivo del rechazo' : undefined });
        if (!result.isConfirmed) return;
        if (!await WarehouseService.rejectOrder(orderId, result.value.trim())) return;
        Utils.showToast('Compra rechazada', 'info');
        this.refresh();
    },
    async receiveOrder(orderId) {
        const result = await SafeHTML.fire({ title: '¿Ha llegado la compra?', text: 'Los artículos se añadirán al inventario.', icon: 'question',
            showCancelButton: true, confirmButtonText: 'Sí, registrar llegada', cancelButtonText: 'Volver', confirmButtonColor: '#0E6B62', reverseButtons: true });
        if (!result.isConfirmed) return;
        if (!await WarehouseService.receiveOrder(orderId)) return;
        Utils.showToast('Llegada registrada: el material ya está en el inventario', 'success');
        this.refresh();
    },
    getStatusBadge(status) {
        // Ver comentario equivalente en StudentRequests.getStatusBadge — las
        // clases "badge-warning"/"badge-primary"/etc. no existen en ningún CSS
        // del proyecto, salían sin color. Devuelve el modificador real de
        // `.wh-status-badge`.
        const map = {
            'pending': 'is-warning',
            'approved': 'is-primary',
            'rejected': 'is-danger',
            'received': 'is-success'
        };
        return map[status] || 'is-secondary';
    }
};

window.PurchaseManagement = PurchaseManagement;
